// CHUNKING.md law 4: a lazy surface in an open dialog/overlay loads behind a
// reserved-space spinner, never a null pop-in — this file owns that shape.
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/cn';

interface ChunkFallbackProps {
  /** 'scrim' precedes a full-screen dialog (bg-black/50 matches DialogOverlay). */
  variant?: 'box' | 'scrim';
  /** Box variant: override the reserved height (`min-h-*`). */
  className?: string;
  /** Scrim variant: scrim click / Escape abandons the pending surface. */
  onDismiss?: () => void;
}

export const ChunkFallback = ({ variant = 'box', className, onDismiss }: ChunkFallbackProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!onDismiss) return undefined;
    // Capture phase: the scrim is top-most, so Escape must not also reach an
    // open Radix dialog underneath and double-close.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onDismiss();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onDismiss]);

  if (variant === 'scrim') {
    return (
      <div
        role="status"
        aria-label={t('CommonText.loading')}
        onClick={onDismiss}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        data-testid="chunk-fallback"
      >
        <Spinner className="size-6 text-white" />
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-label={t('CommonText.loading')}
      className={cn('flex min-h-24 items-center justify-center', className)}
      data-testid="chunk-fallback"
    >
      <Spinner className="size-6" />
    </div>
  );
};
